import { useCallback, useEffect, useMemo, useState } from 'react';
import { mergePlainObjects } from './applyDevStateOverrides';
import { isDevStateLabEnvironment } from './devStateEnvironment';

export const DEV_STATE_LAB_EVENT = 'charitystack:dev-state-lab:change';
const STORAGE_PREFIX = 'charitystack:dev-state-lab:v1';

export const EMPTY_DEV_STATE = Object.freeze({
  merchantPatch: {},
  auth: null,
  readOnly: null,
  signup: {},
});

export function isDevStateLabEnabled() {
  return isDevStateLabEnvironment();
}

export function createDevStateLabStorageKey(scope) {
  return `${STORAGE_PREFIX}:${scope || 'unknown'}`;
}

export function normalizeDevState(state) {
  if (!state || typeof state !== 'object') {
    return { ...EMPTY_DEV_STATE, merchantPatch: {}, signup: {} };
  }

  const signup = {};
  if (typeof state.signup?.hasExistingOrg === 'boolean') {
    signup.hasExistingOrg = state.signup.hasExistingOrg;
  }
  if (typeof state.signup?.signupComplete === 'boolean') {
    signup.signupComplete = state.signup.signupComplete;
  }

  return {
    merchantPatch:
      state.merchantPatch && typeof state.merchantPatch === 'object' ? state.merchantPatch : {},
    auth: state.auth && typeof state.auth === 'object' ? state.auth : null,
    readOnly: typeof state.readOnly === 'boolean' ? state.readOnly : null,
    signup,
  };
}

export function hasDevStateOverrides(state) {
  const normalized = normalizeDevState(state);
  return (
    Object.keys(normalized.merchantPatch).length > 0 ||
    normalized.auth != null ||
    normalized.readOnly != null ||
    Object.keys(normalized.signup).length > 0
  );
}

export function mergeDevState(currentState, patch) {
  const current = normalizeDevState(currentState);
  const nextPatch = patch || {};

  return normalizeDevState({
    merchantPatch:
      nextPatch.merchantPatch !== undefined
        ? mergePlainObjects(current.merchantPatch, nextPatch.merchantPatch)
        : current.merchantPatch,
    auth: nextPatch.auth !== undefined ? nextPatch.auth : current.auth,
    readOnly: nextPatch.readOnly !== undefined ? nextPatch.readOnly : current.readOnly,
    signup:
      nextPatch.signup !== undefined
        ? mergePlainObjects(current.signup, nextPatch.signup)
        : current.signup,
  });
}

export function readDevState(storageKey) {
  if (!isDevStateLabEnabled() || typeof window === 'undefined') {
    return normalizeDevState(null);
  }

  try {
    const raw = window.sessionStorage.getItem(storageKey);
    return normalizeDevState(raw ? JSON.parse(raw) : null);
  } catch (error) {
    console.warn('[DevStateLab] failed to read state', error);
    return normalizeDevState(null);
  }
}

export function writeDevState(storageKey, nextState) {
  if (!isDevStateLabEnabled() || typeof window === 'undefined') {
    return normalizeDevState(null);
  }

  const normalized = normalizeDevState(nextState);
  try {
    if (hasDevStateOverrides(normalized)) {
      window.sessionStorage.setItem(storageKey, JSON.stringify(normalized));
    } else {
      window.sessionStorage.removeItem(storageKey);
    }
    window.dispatchEvent(
      new CustomEvent(DEV_STATE_LAB_EVENT, {
        detail: { storageKey, state: normalized },
      }),
    );
  } catch (error) {
    console.warn('[DevStateLab] failed to write state', error);
  }
  return normalized;
}

export function createDevStateLabScope({ activeOrgId, merchant, clerkUser } = {}) {
  return (
    activeOrgId ||
    merchant?.clerkOrganizationId ||
    merchant?.organizationId ||
    clerkUser?.id ||
    'no-active-org'
  );
}

export function useDevStateLabState(scope) {
  const storageKey = useMemo(() => createDevStateLabStorageKey(scope), [scope]);
  const [state, setState] = useState(() => readDevState(storageKey));

  useEffect(() => {
    setState(readDevState(storageKey));
  }, [storageKey]);

  useEffect(() => {
    if (!isDevStateLabEnabled() || typeof window === 'undefined') return undefined;

    const handleChange = (event) => {
      if (event.detail?.storageKey === storageKey) {
        setState(normalizeDevState(event.detail.state));
      }
    };
    window.addEventListener(DEV_STATE_LAB_EVENT, handleChange);
    return () => window.removeEventListener(DEV_STATE_LAB_EVENT, handleChange);
  }, [storageKey]);

  const replaceState = useCallback(
    (nextStateOrUpdater) => {
      setState((current) => {
        const nextState =
          typeof nextStateOrUpdater === 'function'
            ? nextStateOrUpdater(current)
            : nextStateOrUpdater;
        return writeDevState(storageKey, nextState);
      });
    },
    [storageKey],
  );

  const patchState = useCallback(
    (patch) => {
      replaceState((current) => mergeDevState(current, patch));
    },
    [replaceState],
  );

  const clearState = useCallback(() => {
    replaceState(normalizeDevState(null));
  }, [replaceState]);

  return {
    state,
    storageKey,
    setState: replaceState,
    patchState,
    clearState,
    hasOverrides: hasDevStateOverrides(state),
  };
}
